import React, { useContext } from "react";
import { Checkbox } from "@mui/material";
import * as IconsSVG from "../../../../../assets/iconsSVG";
import { RenderByCargo } from "../../../../../utils/RenderByCargo";
import { Context } from "../../../../../context/Context";
import * as Styles from "../styles";
import {
  ColumnConfig,
  COLUMN_WIDTHS,
  SORT_MAP,
  isCorretor,
  isGerenteVendas,
  canViewEquipeColumn,
  isNonCorretor,
} from "../constants";

interface Props {
  visibleColumns: ColumnConfig[];
  codigoCargo: string | null;
  usuarioGerenteOnline?: boolean;
  active: boolean;
  onCheckAll: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isShared: boolean;
}

const ORDER_KEYS = [
  "order1",
  "order2",
  "order3",
  "order4",
  "order5",
  "order6",
  "order7",
  "order8",
  "order9",
  "order10",
  "order11",
  "order12",
  "order13",
];

const resetOrders = () =>
  ORDER_KEYS.reduce((acc, key) => ({ ...acc, [key]: null }), {});

const SortableHeader: React.FC<{
  label: string;
  columnId: string;
  width?: number;
  cursor?: string;
  filters: any;
  setFilters: any;
  orderKey?: string;
}> = ({ label, columnId, width, cursor, filters, setFilters, orderKey }) => {
  const sortConfig = SORT_MAP[columnId];
  const currentOrderKey = orderKey || sortConfig?.orderKey;

  const handleSort = () => {
    if (!sortConfig) return;
    setFilters({
      ...filters,
      OrdenacaoLeads: sortConfig.ordenacaoLeads,
      OrdenacaoCrescente: !filters?.[currentOrderKey],
      ...resetOrders(),
      [currentOrderKey]: !filters?.[currentOrderKey],
    });
  };

  return (
    <Styles.Th
      style={{
        width: width || COLUMN_WIDTHS[columnId],
        cursor: cursor || "pointer",
      }}
      onClick={sortConfig ? handleSort : undefined}
    >
      <p className="reorder">
        {label}
        {sortConfig && (
          <IconsSVG.ArrowDownIcon
            className={filters?.[currentOrderKey] ? "arrowIcon" : undefined}
            style={{ marginLeft: 8 }}
          />
        )}
      </p>
    </Styles.Th>
  );
};

export const TableHeader: React.FC<Props> = ({
  visibleColumns,
  codigoCargo,
  usuarioGerenteOnline = false,
  active,
  onCheckAll,
  isShared,
}) => {
  const { filters, setFilters } = useContext(Context);

  const renderColumn = (column: ColumnConfig) => {
    switch (column.id) {
      case "select":
        return (
          <Styles.FirstFixedTh key={column.id} style={{ width: 36 }}>
            <Checkbox size="small" checked={active} onChange={onCheckAll} />
          </Styles.FirstFixedTh>
        );

      case "actions":
        // Gerente online é somente leitura: sem coluna de ações.
        if (usuarioGerenteOnline) return null;
        return (
          <RenderByCargo
            key={column.id}
            cargo={codigoCargo}
            isCorretor={<Styles.SecondFixedTh>Ações</Styles.SecondFixedTh>}
          />
        );

      case "lead": {
        const leftPosition =
          isNonCorretor(codigoCargo) && !isCorretor(codigoCargo) ? 33 : 160;
        return (
          <Styles.ThirdFixedTh
            key={column.id}
            style={{ width: 120, left: leftPosition }}
            onClick={() => {
              setFilters({
                ...filters,
                OrdenacaoLeads: 1,
                OrdenacaoCrescente: !filters?.order1,
                ...resetOrders(),
                order1: !filters?.order1,
              });
            }}
          >
            <p className="reorder">
              Lead
              <IconsSVG.ArrowDownIcon
                className={filters?.order1 && "arrowIcon"}
              />
            </p>
          </Styles.ThirdFixedTh>
        );
      }

      case "subetapa":
        return (
          <Styles.Th key={column.id} style={{ width: 120 }}>
            <p className="reorder">
              Subetapa
              <IconsSVG.ArrowDownIcon
                className={filters?.order1 && "arrowIcon"}
                style={{ marginLeft: 8 }}
              />
            </p>
          </Styles.Th>
        );

      case "obs":
        return (
          <SortableHeader
            key={column.id}
            label="Obs"
            columnId="obs"
            filters={filters}
            setFilters={setFilters}
          />
        );

      case "ultimaTarefa":
        return isCorretor(codigoCargo) ? (
          <Styles.Th key={column.id} style={{ width: 60, cursor: "auto" }}>
            <p className="reorder">Última Tarefa</p>
          </Styles.Th>
        ) : null;

      case "telefone":
        return (
          <SortableHeader
            key={column.id}
            label="Telefone"
            columnId="telefone"
            filters={filters}
            setFilters={setFilters}
          />
        );

      case "email":
        return isCorretor(codigoCargo) ? (
          <SortableHeader
            key={column.id}
            label="E-mail"
            columnId="email"
            filters={filters}
            setFilters={setFilters}
          />
        ) : null;

      case "corretorResponsavel":
        return isNonCorretor(codigoCargo) ? (
          <SortableHeader
            key={column.id}
            label="Corretor responsável"
            columnId="corretorResponsavel"
            filters={filters}
            setFilters={setFilters}
          />
        ) : null;

      case "ultimoResponsavel":
        return isGerenteVendas(codigoCargo) ? (
          <SortableHeader
            key={column.id}
            label="Último Responsável"
            columnId="ultimoResponsavel"
            filters={filters}
            setFilters={setFilters}
          />
        ) : null;

      case "equipe":
        return canViewEquipeColumn(codigoCargo, usuarioGerenteOnline) ? (
          <SortableHeader
            key={column.id}
            label="Equipe"
            columnId="equipe"
            filters={filters}
            setFilters={setFilters}
          />
        ) : null;

      case "produto":
        return (
          <Styles.Th key={column.id} style={{ width: 95, cursor: "auto" }}>
            Produto
          </Styles.Th>
        );

      case "etiqueta":
        return isCorretor(codigoCargo) ||
          (codigoCargo === "219.840.012" && !isShared) ? (
          <SortableHeader
            key={column.id}
            label="Etiqueta"
            columnId="etiqueta"
            filters={filters}
            setFilters={setFilters}
          />
        ) : null;

      case "dataCriacao":
        return (
          <SortableHeader
            key={column.id}
            label="Criação"
            columnId="dataCriacao"
            filters={filters}
            setFilters={setFilters}
          />
        );

      case "expiraEm":
        return (
          <SortableHeader
            key={column.id}
            label="Expira"
            columnId="expiraEm"
            filters={filters}
            setFilters={setFilters}
          />
        );

      case "dataAtualizacao":
        return (
          <SortableHeader
            key={column.id}
            label="Atualização"
            columnId="dataAtualizacao"
            filters={filters}
            setFilters={setFilters}
          />
        );

      case "etapa":
        return (
          <SortableHeader
            key={column.id}
            label="Etapa"
            columnId="etapa"
            filters={filters}
            setFilters={setFilters}
          />
        );

      case "temperatura":
        return (
          <SortableHeader
            key={column.id}
            label="Temp."
            columnId="temperatura"
            filters={filters}
            setFilters={setFilters}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Styles.THead>
      <Styles.Tr>{visibleColumns.map(renderColumn)}</Styles.Tr>
    </Styles.THead>
  );
};
